import React, { useMemo } from 'react';

import { Box } from '@material-ui/core';
import { useTranslation } from 'react-i18next';

import InputWithDebouncedAction from 'components/InputWithDebouncedAction/InputWithDebouncedAction';
import type { OwnerWorkspaceFilters } from 'config/api/assetRopa/assetRopaFilter.types';
import type { OwnerWorkspaceFilterOptions } from 'config/api/assetRopa/assetRopaWorkspace.helpers';
import processing_activities_messages from 'messages/processing_activities_messages';
import ProcessingActivitiesFilterSelect from 'pages/ProcessingActivities/ProcessingActivitiesPage/_components/ProcessingActivitiesFilterSelect';

import useStyles from '../../ProcessingActivitiesAssetReviewsPage/ProcessingActivitiesAssetReviewsPage.styles';

type CompletenessMessageKey = keyof typeof processing_activities_messages.asset_ropa.owner_workspace.completeness_status;
type GdprRelevanceMessageKey = keyof typeof processing_activities_messages.asset_ropa.owner_workspace.gdpr_relevance;

const COMPLETENESS_OPTIONS: CompletenessMessageKey[] = ['complete', 'incomplete', 'no_processing_activities'];
const GDPR_RELEVANCE_OPTIONS: GdprRelevanceMessageKey[] = ['currently_indicated', 'no_longer_indicated', 'all'];

type Props = {
  filterOptions: OwnerWorkspaceFilterOptions;
  filters: OwnerWorkspaceFilters;
  onFiltersChange: (filters: OwnerWorkspaceFilters) => void;
};

const OwnerWorkspaceFiltersToolbar: React.FC<Props> = ({ filterOptions, filters, onFiltersChange }) => {
  const { t } = useTranslation();
  const styles = useStyles();
  const messages = processing_activities_messages.asset_ropa.owner_workspace.filters;
  const filterMessages = processing_activities_messages.asset_ropa.filters;
  const formatFilterAllLabel = (label: string) => t(processing_activities_messages.filters.option_all, { label });

  const assetTypeOptions = useMemo(
    () =>
      filterOptions.assetTypes.map(assetType => ({
        value: String(assetType.id),
        label: assetType.name,
      })),
    [filterOptions.assetTypes],
  );

  const completenessOptions = useMemo(
    () =>
      COMPLETENESS_OPTIONS.map(status => ({
        value: status,
        label: t(processing_activities_messages.asset_ropa.owner_workspace.completeness_status[status]),
      })),
    [t],
  );

  const gdprRelevanceOptions = useMemo(() => {
    const gdprRelevanceLabel = t(messages.gdpr_relevance);

    return GDPR_RELEVANCE_OPTIONS.map(status => ({
      value: status,
      label:
        status === 'all'
          ? formatFilterAllLabel(gdprRelevanceLabel)
          : t(processing_activities_messages.asset_ropa.owner_workspace.gdpr_relevance[status]),
    }));
  }, [messages.gdpr_relevance, t]);

  const booleanOptions = useMemo(
    () => [
      { value: 'true', label: t(filterMessages.yes) },
      { value: 'false', label: t(filterMessages.no) },
    ],
    [filterMessages.no, filterMessages.yes, t],
  );

  const dpiaOptions = useMemo(
    () => [
      { value: 'indicated', label: t(filterMessages.dpia_indicated) },
      { value: 'none', label: t(filterMessages.dpia_none) },
    ],
    [filterMessages.dpia_indicated, filterMessages.dpia_none, t],
  );

  const updateFilter = <K extends keyof OwnerWorkspaceFilters>(key: K, value: OwnerWorkspaceFilters[K]) => {
    onFiltersChange({ ...filters, [key]: value });
  };

  const hasAssetTypeOptions = filterOptions.assetTypes.length > 0;

  return (
    <Box className={styles.toolbarSection}>
      <Box className={styles.filtersRow}>
        <div className={styles.searchInputCell}>
          <InputWithDebouncedAction
            action={value => updateFilter('search', value)}
            className={styles.searchInputLabelEllipsis}
            formControl={{ size: 'small' }}
            label={t(messages.search_placeholder)}
            value={filters.search}
          />
        </div>

        {hasAssetTypeOptions && (
          <ProcessingActivitiesFilterSelect
            allLabel={formatFilterAllLabel(t(messages.asset_type))}
            ariaLabel={t(messages.asset_type)}
            onChange={value => updateFilter('assetTypeId', value ? Number(value) : '')}
            options={assetTypeOptions}
            value={filters.assetTypeId === '' ? '' : String(filters.assetTypeId)}
          />
        )}

        <ProcessingActivitiesFilterSelect
          allLabel={formatFilterAllLabel(t(messages.completeness))}
          ariaLabel={t(messages.completeness)}
          onChange={value => updateFilter('completeness', value as OwnerWorkspaceFilters['completeness'])}
          options={completenessOptions}
          value={filters.completeness}
        />

        <ProcessingActivitiesFilterSelect
          allLabel={formatFilterAllLabel(t(messages.gdpr_relevance))}
          ariaLabel={t(messages.gdpr_relevance)}
          includeEmptyOption={false}
          onChange={value => updateFilter('gdprRelevance', value as OwnerWorkspaceFilters['gdprRelevance'])}
          options={gdprRelevanceOptions}
          value={filters.gdprRelevance}
        />

        <ProcessingActivitiesFilterSelect
          allLabel={formatFilterAllLabel(t(filterMessages.dpia_state))}
          ariaLabel={t(filterMessages.dpia_state)}
          onChange={value => updateFilter('dpiaState', value as OwnerWorkspaceFilters['dpiaState'])}
          options={dpiaOptions}
          value={filters.dpiaState}
        />

        <ProcessingActivitiesFilterSelect
          allLabel={formatFilterAllLabel(t(filterMessages.sensitive_personal_data))}
          ariaLabel={t(filterMessages.sensitive_personal_data)}
          onChange={value => updateFilter('sensitivePersonalData', value as OwnerWorkspaceFilters['sensitivePersonalData'])}
          options={booleanOptions}
          value={filters.sensitivePersonalData}
        />

        <ProcessingActivitiesFilterSelect
          allLabel={formatFilterAllLabel(t(filterMessages.third_country_processing))}
          ariaLabel={t(filterMessages.third_country_processing)}
          onChange={value => updateFilter('thirdCountryProcessing', value as OwnerWorkspaceFilters['thirdCountryProcessing'])}
          options={booleanOptions}
          value={filters.thirdCountryProcessing}
        />
      </Box>
    </Box>
  );
};

export default OwnerWorkspaceFiltersToolbar;
